import type { CarbonProject } from "@/mocks/carbono";
import CarbonStatusBadge from "./CarbonStatusBadge";

interface CarbonProjectDetailDrawerProps {
  project: CarbonProject | null;
  onClose: () => void;
}

export default function CarbonProjectDetailDrawer({ project, onClose }: CarbonProjectDetailDrawerProps) {
  if (!project) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
      />
      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-lg bg-white z-50 shadow-xl overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="text-xs text-foreground-500 mb-1">{project.type}</div>
              <h2 className="text-lg font-bold text-foreground-900 font-heading">{project.name}</h2>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-md hover:bg-background-100 flex items-center justify-center transition-colors"
              aria-label="Fechar"
            >
              <i className="ri-close-line w-full h-full flex items-center justify-center text-lg text-foreground-500" />
            </button>
          </div>

          <CarbonStatusBadge status={project.status} />

          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="bg-background-50 rounded-lg p-3">
              <div className="text-[11px] text-foreground-400 mb-0.5">Cliente</div>
              <div className="text-sm font-medium text-foreground-800">{project.client}</div>
            </div>
            <div className="bg-background-50 rounded-lg p-3">
              <div className="text-[11px] text-foreground-400 mb-0.5">Município</div>
              <div className="text-sm font-medium text-foreground-800">{project.municipality}/{project.uf}</div>
            </div>
            <div className="bg-background-50 rounded-lg p-3">
              <div className="text-[11px] text-foreground-400 mb-0.5">Área vinculada</div>
              <div className="text-sm font-medium text-foreground-800">{project.areaHa} ha</div>
            </div>
            <div className="bg-background-50 rounded-lg p-3">
              <div className="text-[11px] text-foreground-400 mb-0.5">Árvores consideradas</div>
              <div className="text-sm font-medium text-foreground-800">{project.treesCount.toLocaleString("pt-BR")}</div>
            </div>
          </div>

          {/* Metrics */}
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-foreground-900 mb-3">Estimativas Principais</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-primary-50 rounded-lg p-3 border border-primary-100">
                <div className="text-[11px] text-primary-600 mb-0.5">CO2e estimado</div>
                <div className="text-lg font-bold text-primary-700 font-heading">{project.co2eEstimated.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} <span className="text-xs font-normal">t</span></div>
              </div>
              <div className="bg-accent-50 rounded-lg p-3 border border-accent-100">
                <div className="text-[11px] text-accent-600 mb-0.5">Carbono estimado</div>
                <div className="text-lg font-bold text-accent-700 font-heading">{project.carbonEstimated.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} <span className="text-xs font-normal">t C</span></div>
              </div>
              <div className="bg-secondary-50 rounded-lg p-3 border border-secondary-100">
                <div className="text-[11px] text-secondary-600 mb-0.5">Biomassa estimada</div>
                <div className="text-lg font-bold text-secondary-700 font-heading">{project.biomassEstimated.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} <span className="text-xs font-normal">t</span></div>
              </div>
            </div>
          </div>

          {/* Confidence & Quality */}
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-foreground-900 mb-3">Qualidade dos Dados</h3>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-foreground-600">Nível de confiança</span>
                  <span className="font-semibold text-foreground-800">{project.confidenceLevel}%</span>
                </div>
                <div className="w-full bg-background-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${project.confidenceLevel >= 80 ? "bg-primary-500" : project.confidenceLevel >= 60 ? "bg-caution-500" : "bg-warning-500"}`}
                    style={{ width: `${project.confidenceLevel}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-foreground-600">Metodologia aplicada</span>
                  <span className="font-semibold text-foreground-800">{project.methodology}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Limitations */}
          <div className="mt-6 bg-caution-50 rounded-lg p-4 border border-caution-100">
            <div className="flex items-start gap-2">
              <i className="ri-information-line text-caution-600 w-4 h-4 flex items-center justify-center shrink-0 mt-0.5" />
              <div>
                <div className="text-xs font-semibold text-caution-700 mb-1">Limitações da estimativa</div>
                <div className="text-xs text-caution-600 leading-relaxed">
                  Os valores apresentados são estimativas preliminares. A precisão depende da qualidade dos dados de campo, identificação botânica e equações alométricas disponíveis. Não constitui certificação oficial de crédito de carbono.
                </div>
              </div>
            </div>
          </div>

          {/* History */}
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-foreground-900 mb-3">Histórico</h3>
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-xs">
                <i className="ri-calendar-check-line text-primary-600 w-4 h-4 flex items-center justify-center shrink-0" />
                <div>
                  <span className="font-medium text-foreground-700">Projeto criado</span>
                  <span className="text-foreground-400 ml-2">{project.createdAt}</span>
                </div>
              </div>
              <div className="flex items-start gap-2 text-xs">
                <i className="ri-refresh-line text-accent-600 w-4 h-4 flex items-center justify-center shrink-0" />
                <div>
                  <span className="font-medium text-foreground-700">Última atualização das estimativas</span>
                  <span className="text-foreground-400 ml-2">{project.lastUpdate}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-8 pt-4 border-t border-background-200">
            <button className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap">
              Atualizar estimativa
            </button>
            <button className="flex-1 px-4 py-2.5 text-sm font-semibold text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors whitespace-nowrap">
              Gerar relatório
            </button>
            <button className="px-4 py-2.5 text-sm font-semibold text-foreground-600 bg-background-50 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap">
              Mapa
            </button>
          </div>
        </div>
      </div>
    </>
  );
}